import Image from "next/image";
import { useCallback, useState } from "react";
import { BsTwitterX } from "react-icons/bs";
import { IoLogoUsd } from "react-icons/io5";
import {
  LuCalendar,
  LuChevronDown,
  LuGem,
  LuLandmark,
  LuScrollText,
  LuSmile,
  LuThumbsDown,
  LuThumbsUp,
  LuUser as LuUser2,
} from "react-icons/lu";

import { PopoverFilterSortActions } from "@/components/shared/popover-filter-sort-actions";
import {
  BasicSortRecord,
  NumericWithTimeSortRecord,
  SortsRecordsSet,
} from "@/components/shared/sort-sections";
import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  PopoverDrawer,
  PopoverDrawerContent,
  PopoverDrawerTrigger,
  usePopoverDrawerContext,
} from "@/components/ui/popover-drawer";
import { WHALE_ICON } from "@/utils/constants";

import { DiscoverFilterFields } from "../discover-sort-constants";
import { useDiscoverContext } from "./discover-context";

export const DiscoverSortPopover = () => {
  const [open, setOpen] = useState(false);

  return (
    <PopoverDrawer
      historyName="discover-sort"
      onOpenChange={setOpen}
      open={open}
      title="Discover sort"
    >
      <PopoverDrawerTrigger asChild>
        <Button variant="link" className="flex items-center gap-1 text-muted">
          <span className="text-xs">Sort</span>
          <LuChevronDown />
        </Button>
      </PopoverDrawerTrigger>
      <DiscoverSortPopoverContent />
    </PopoverDrawer>
  );
};

const DiscoverSortPopoverContent = () => {
  const {
    onFiltersApply,
    setCurrentSort,
    currentSort,
    isSortActive,
    resetDiscoverSort,
  } = useDiscoverContext();

  const methods = {
    setCurrentSort,
    currentSort,
    isSortActive,
  };
  const { close } = usePopoverDrawerContext();

  const onSubmitClick = useCallback(() => {
    onFiltersApply();
    close();
  }, [onFiltersApply, close]);

  const onResetClick = useCallback(() => {
    resetDiscoverSort();
    close();
  }, [close, resetDiscoverSort]);

  return (
    <PopoverDrawerContent
      align="start"
      side="bottom"
      drawerDescription="Choose discover sort order."
      className="
        custom-scrollbar w-screen overflow-y-auto p-6 pb-0
        shadow-xl md:max-h-[min(72vh,450px)] md:min-h-[450px]
        md:w-[600px] md:pb-6
      "
    >
      <Accordion type="single" collapsible>
        <div className="flex flex-col gap-5">
          <SortsRecordsSet name="Profile">
            <BasicSortRecord<DiscoverFilterFields>
              {...methods}
              name={DiscoverFilterFields.joinedFocusDate}
              label="Joined Date"
              icon={<LuCalendar />}
            />
          </SortsRecordsSet>
          <SortsRecordsSet name="Reactions">
            <NumericWithTimeSortRecord<DiscoverFilterFields>
              {...methods}
              label="Total Reactions Received"
              name={DiscoverFilterFields.totalReactionsReceived}
              icon={<LuSmile />}
            />
            <NumericWithTimeSortRecord<DiscoverFilterFields>
              {...methods}
              label="Total Positive Reactions Received"
              name={DiscoverFilterFields.totalPositiveReactionsReceived}
              icon={<LuThumbsUp />}
            />
            <NumericWithTimeSortRecord<DiscoverFilterFields>
              {...methods}
              label="Total Negative Reactions Received"
              name={DiscoverFilterFields.totalNegativeReactionsReceived}
              icon={<LuThumbsDown />}
            />
            <NumericWithTimeSortRecord<DiscoverFilterFields>
              {...methods}
              label="Total Reactions Given"
              name={DiscoverFilterFields.totalReactionsGiven}
              icon={<LuSmile />}
            />
            <NumericWithTimeSortRecord<DiscoverFilterFields>
              {...methods}
              label="Total Positive Reactions Given"
              name={DiscoverFilterFields.totalPositiveReactionsGiven}
              icon={<LuThumbsUp />}
            />
            <NumericWithTimeSortRecord<DiscoverFilterFields>
              {...methods}
              label="Total Negative Reactions Given"
              name={DiscoverFilterFields.totalNegativeReactionsGiven}
              icon={<LuThumbsDown />}
            />
          </SortsRecordsSet>

          <SortsRecordsSet name="Diamonds">
            <NumericWithTimeSortRecord<DiscoverFilterFields>
              {...methods}
              label="Total Tips Received USD"
              name={DiscoverFilterFields.totalDiamondsUsdCentsReceived}
              icon={<LuGem />}
            />
            <NumericWithTimeSortRecord<DiscoverFilterFields>
              {...methods}
              label="Total Tips Sent USD"
              name={DiscoverFilterFields.totalDiamondsUsdCentsSent}
              icon={<LuGem />}
            />
          </SortsRecordsSet>

          <SortsRecordsSet name="Followers">
            <BasicSortRecord<DiscoverFilterFields>
              {...methods}
              label="Total Followers"
              name={DiscoverFilterFields.totalFollowers}
              icon={<LuUser2 />}
            />
            <BasicSortRecord<DiscoverFilterFields>
              {...methods}
              label="Total Whale Followers"
              name={DiscoverFilterFields.totalWhaleFollowers}
              icon={
                <Image
                  src={WHALE_ICON}
                  unoptimized
                  alt="whale"
                  {...{ width: 20, height: 20 }}
                />
              }
            />
          </SortsRecordsSet>

          <SortsRecordsSet name="Wealth">
            <BasicSortRecord<DiscoverFilterFields>
              {...methods}
              label="Total Assets USD"
              name={DiscoverFilterFields.totalAssetsBalanceUsdCents}
              icon={<IoLogoUsd />}
            />
            <BasicSortRecord<DiscoverFilterFields>
              {...methods}
              label="Total Liquid Assets USD"
              name={DiscoverFilterFields.totalLiquidAssetsBalanceUsdCents}
              icon={<IoLogoUsd />}
            />
            <BasicSortRecord<DiscoverFilterFields>
              {...methods}
              label="DESO Balance"
              name={DiscoverFilterFields.desoBalanceNanos}
              icon={<IoLogoUsd />}
            />
            <BasicSortRecord<DiscoverFilterFields>
              {...methods}
              label="USDC Balance"
              name={DiscoverFilterFields.usdcBalanceUsdCents}
              icon={<IoLogoUsd />}
            />
            <BasicSortRecord<DiscoverFilterFields>
              {...methods}
              label="USDT Balance"
              name={DiscoverFilterFields.usdtBalanceUsdCents}
              icon={<IoLogoUsd />}
            />
            <BasicSortRecord<DiscoverFilterFields>
              {...methods}
              label="Market Cap USD"
              name={DiscoverFilterFields.marketCapUsd}
              icon={<IoLogoUsd />}
            />
          </SortsRecordsSet>
          <SortsRecordsSet name="Posts">
            <NumericWithTimeSortRecord<DiscoverFilterFields>
              {...methods}
              label="Total Posts Created"
              name={DiscoverFilterFields.totalPosts}
              icon={<LuScrollText />}
            />
            <NumericWithTimeSortRecord<DiscoverFilterFields>
              {...methods}
              label="Total Post Views"
              name={DiscoverFilterFields.totalFocusPostViews}
              icon={<LuScrollText />}
            />
            <NumericWithTimeSortRecord<DiscoverFilterFields>
              {...methods}
              label="Total Whale Post Views"
              name={DiscoverFilterFields.totalFocusPostWhaleViews}
              icon={<LuScrollText />}
            />
            <NumericWithTimeSortRecord<DiscoverFilterFields>
              {...methods}
              label="Total Viewed Posts"
              name={DiscoverFilterFields.totalViewedFocusPosts}
              icon={<LuScrollText />}
            />
          </SortsRecordsSet>

          <SortsRecordsSet name="Earnings">
            <NumericWithTimeSortRecord<DiscoverFilterFields>
              {...methods}
              label="Total Earnings USD"
              name={DiscoverFilterFields.totalFocusEarningsUsdCents}
              icon={<LuLandmark />}
            />
            <NumericWithTimeSortRecord<DiscoverFilterFields>
              {...methods}
              label="Total Payed Messages Earnings USD"
              name={DiscoverFilterFields.totalPaidMessageEarningsUsdCents}
              icon={<LuLandmark />}
            />
            <NumericWithTimeSortRecord<DiscoverFilterFields>
              {...methods}
              label="Total Subscription Earnings USD"
              name={DiscoverFilterFields.totalSubscriptionEarningsUsdCents}
              icon={<LuLandmark />}
            />
            <NumericWithTimeSortRecord<DiscoverFilterFields>
              {...methods}
              label="Total Joining Reward USD"
              name={DiscoverFilterFields.totalJoiningRewardUsdCents}
              icon={<LuLandmark />}
            />
            <NumericWithTimeSortRecord<DiscoverFilterFields>
              {...methods}
              label="Unlocked Joining Reward USD"
              name={DiscoverFilterFields.unlockedJoiningRewardUsdCents}
              icon={<LuLandmark />}
            />
            <NumericWithTimeSortRecord<DiscoverFilterFields>
              {...methods}
              label="Locked Joining Reward USD"
              name={DiscoverFilterFields.lockedJoiningRewardUsdCents}
              icon={<LuLandmark />}
            />
            <NumericWithTimeSortRecord<DiscoverFilterFields>
              {...methods}
              label="Total Referral Rewards USD"
              name={DiscoverFilterFields.totalReferralRewardsUsdCents}
              icon={<LuLandmark />}
            />
            <NumericWithTimeSortRecord<DiscoverFilterFields>
              {...methods}
              label="Unlocked Referral Rewards USD"
              name={DiscoverFilterFields.unlockedReferralRewardsUsdCents}
              icon={<LuLandmark />}
            />
            <NumericWithTimeSortRecord<DiscoverFilterFields>
              {...methods}
              label="Locked Referral Rewards USD"
              name={DiscoverFilterFields.lockedReferralRewardsUsdCents}
              icon={<LuLandmark />}
            />
          </SortsRecordsSet>
          <SortsRecordsSet name="X (Twitter)">
            <BasicSortRecord<DiscoverFilterFields>
              {...methods}
              label="X (Twitter) Followers"
              name={DiscoverFilterFields.twitterTotalFollowers}
              icon={<BsTwitterX />}
            />
            <BasicSortRecord<DiscoverFilterFields>
              {...methods}
              label="X (Twitter) Following"
              name={DiscoverFilterFields.twitterTotalFollowing}
              icon={<BsTwitterX />}
            />
            <BasicSortRecord<DiscoverFilterFields>
              {...methods}
              label="X (Twitter) Total Tweets Created"
              name={DiscoverFilterFields.twitterTotalTweets}
              icon={<BsTwitterX />}
            />
          </SortsRecordsSet>
        </div>
        <PopoverFilterSortActions
          className="
            sticky inset-x-auto bottom-0 z-10 -mx-6 mt-4 w-auto
            rounded-none border-x-0 border-b-0 bg-card/95 backdrop-blur-sm
          "
          customSubmitButton={
            <Button variant="default" size="lg" onClick={onSubmitClick}>
              Apply
            </Button>
          }
          onResetClick={onResetClick}
        />
      </Accordion>
    </PopoverDrawerContent>
  );
};
